import { useCallback, useEffect, useRef } from 'react';

import { useFragment as useApolloFragment } from '@apollo/client/react';
import { Button, Skeleton } from '@mui/material';
import { sanitizeHtml } from '@realadvisor/html-sanitizer';

import {
  type FormDefinitionType,
  RaForm,
  type RaFormOnChange,
} from '@modules/_shared/components/form/RaForm';
import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { STEP_INTRODUCTION_FRAGMENT } from '@modules/reports/cma-reports/cmaReportsQueries';
import {
  FooterActions,
  type FooterActionsRef,
} from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useUpdateCmaReport } from '@modules/reports/shared/utils';
import { useDebouncedHandler } from '@shared/hooks/debounce';

type StepIntroductionForm = {
  introduction: string;
};

const getIntroductionFromMarkup = (markup: string | null): string | null => {
  if (!markup) {
    return null;
  }
  try {
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const el = doc.getElementById('introduction-template');
    if (el?.innerHTML) {
      return sanitizeHtml(el.innerHTML.replace(/\n\s*/g, ''));
    }
  } catch (error) {
    console.error('Error extracting introduction from markup:', error);
  }
  return null;
};

const StepIntroduction = (props: ReportComponentProps) => {
  const { reportId: cmaReportId, setStep, step } = props;
  const { markup } = useReportContext();

  const { data, complete } = useApolloFragment({
    fragment: STEP_INTRODUCTION_FRAGMENT,
    fragmentName: 'StepIntroduction',
    from: {
      __typename: 'cma_reports',
      id: cmaReportId,
    },
  });

  const [updateCmaReport, updating] = useUpdateCmaReport(
    cmaReportId,
    'page-introduction',
  );

  const footerActionsRef = useRef<FooterActionsRef | null>(null);

  const fillFromTemplate = useCallback(() => {
    const introduction = getIntroductionFromMarkup(markup);
    if (introduction == null) {
      return null;
    }
    footerActionsRef.current?.setValue('introduction', introduction, {
      shouldDirty: true,
    });
    return introduction;
  }, [markup]);

  const update = useCallback(
    async (formData?: Partial<StepIntroductionForm>) => {
      await updateCmaReport({
        introduction: formData?.introduction ?? '',
      });
    },
    [updateCmaReport],
  );

  const resetFromTemplate = useCallback(() => {
    const introduction = fillFromTemplate();
    if (introduction != null) {
      void update({ introduction });
    }
  }, [fillFromTemplate, update]);

  const stepIntroductionFormDefinition: FormDefinitionType<StepIntroductionForm> =
    useCallback(
      ({ t }) => [
        {
          name: 'introduction',
          label: t('Introduction'),
          preserveLineBreaks: true,
          type: 'rich-text',
          format: 'html',
          gridProps: { md: 12 },
          actions: () => (
            <Button variant="text" size="small" onClick={resetFromTemplate}>
              {t('reset')}
            </Button>
          ),
        },
      ],
      [resetFromTemplate],
    );

  useEffect(() => {
    if (!data?.introduction && markup) {
      fillFromTemplate();
    }
  }, [data, markup, fillFromTemplate]);

  const debouncedUpdate = useDebouncedHandler(300, update);

  const onChangeHandler: RaFormOnChange<StepIntroductionForm> = (
    formData,
    _,
    type,
  ) => {
    if (type === 'change') {
      debouncedUpdate(formData);
    }
  };

  const onSubmit = useCallback(
    async (formData: StepIntroductionForm) => {
      await update(formData);
      setStep(step + 1);
    },
    [update, setStep, step],
  );

  if (!complete) {
    return (
      <Skeleton height={400} sx={{ my: 3, mx: 1 }} variant="rectangular" />
    );
  }

  return (
    <RaForm
      freezeInitialDefaultValues={true}
      formDefinition={stepIntroductionFormDefinition}
      onSubmit={onSubmit}
      defaultValues={{
        introduction: data?.introduction ?? '',
      }}
      onChange={onChangeHandler}
      actionButtonsComponent={
        <FooterActions {...props} ref={footerActionsRef} updating={updating} />
      }
      contentScrollable
    />
  );
};

export default StepIntroduction;
